import React, { useState, useEffect, useRef } from 'react';
import { Asset, formatData, keyCode } from '../helpers';
import { Button, Icon, SearchResult, Tabs, Input } from '../components';
import { useGlobalState, useQueryParams, useSearch } from '../hooks';
import { statisticsService, userService } from '../services';

/**
 * Picks the singular or the plural form for a count, for the figures under the cards.
 * @param {number} count
 * @param {string} singular
 * @param {string} plural
 * @returns {string}
 */
const label = (count, singular, plural) => (count === 1 ? singular : plural);

export const Home = () => {
  const { queryParams, setHistory } = useQueryParams();

  const [showSearchTip, setShowSearchTip] = useState(false);
  const [showAllSearchTips, setShowAllSearchTips] = useState(false);
  const [searchTipIndex, setSearchTipIndex] = useState();

  const [loading, setLoading] = useState(false);
  const [activeObject, setActiveObject] = useState(queryParams.object);
  // How much this installation holds, shown under each card's button. Undefined until it arrives.
  const [assetCount, setAssetCount] = useState();
  // How many assets are assigned to the person looking at the page. Zero for most people, which is
  // why the line that shows it renders only when there is something to show.
  const [ownedItemsCount, setOwnedItemsCount] = useState(0);

  const [loggedInUser] = useGlobalState('loggedInUser');

  const refTabContent = useRef();
  const refShowAllSearchTipsButton = useRef();
  const refSearchInput = useRef();
  const refClearSearchButton = useRef();

  const {
    searchTerm,
    searchResults,
    searchResultsCount,
    setSearchTerm,
    fetchSearchResults,
    clearSearchResults,
    invalidSearchTerm,
    loadNextPage,
  } = useSearch({
    setLoading,
    objectName: activeObject,
  });

  useEffect(() => {
    if (searchTerm !== '') {
      fetchSearchResults(undefined, false, 1);
    }
  }, [activeObject]);

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    statisticsService
      .getAssetCount(controller)
      .then(res => res && setAssetCount(res.asset_count))
      // Deliberately silent: the figures say how much is in here, and a card without them still
      // does its job. A message about them would be the first thing a user saw on the start page.
      .catch(() => {});

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!loggedInUser?.user_id) return undefined;

    const controller = new AbortController();

    userService
      .getOwnedItemsCount(loggedInUser.user_id, controller)
      .then(
        res =>
          res &&
          setOwnedItemsCount(
            (res.owned_items_count || [])
              // Everything a person can be given except an OIDC client, which is configuration rather
              // than an asset anyone browses. The Assigned Assets tab this links to counts the same
              // set, by naming each kind.
              .filter(x => x.object_name !== 'oidc_client')
              .reduce((total, x) => total + Number(x.owned_items_count), 0)
          )
      )
      // Silent for the same reason as the figures above: the line is a shortcut, and its absence
      // costs the user nothing.
      .catch(() => {});

    return () => controller.abort();
  }, [loggedInUser?.user_id]);

  const onKeyDown = e => {
    if ((e.ctrlKey || e.metaKey) && e.keyCode === keyCode.K) {
      e.preventDefault();
      refSearchInput.current.focus();
    }

    if (e.keyCode === keyCode.ESCAPE) {
      setTimeout(() => refSearchInput.current.blur(), 100);
    }
  };

  const handleInputChange = async event => {
    let { /*name,*/ value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      //name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      //name = event.name;
      value = event.value;
    }

    setSearchTerm(value);

    if (value.length > 1) {
      await fetchSearchResults(value, false, 1);

      setShowSearchTip(false);
    } else {
      setShowSearchTip(true);
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    // Form validation
    // ...
  };

  const onObjectChange = object => {
    setActiveObject(object);
    setHistory({ object: object, filter: '', sort: undefined });
  };

  const handleFocus = () => {
    if (!(searchTerm && searchTerm.length > 1)) {
      setShowSearchTip(true);
    }
    !searchTipIndex &&
      setSearchTipIndex(Math.floor(Math.random() * (searchTipList.length - 1 + 1)) + 1);
  };

  const toggleShowAllSearchTips = () => {
    setShowAllSearchTips(!showAllSearchTips);
    refSearchInput.current.focus();
  };

  const handleBlur = el => {
    const activeElement = el.relatedTarget;
    if (
      activeElement === null ||
      !(
        activeElement === refShowAllSearchTipsButton.current ||
        activeElement === refClearSearchButton.current
      )
    ) {
      setShowSearchTip(false);
      setShowAllSearchTips(false);
      setSearchTipIndex();
    }
  };

  const loadMore = () => {
    loadNextPage();
  };

  const searchTipList = [
    {
      title: 'Default Search',
      tip: 'The default behaviour is wildcard match with "and"-logic',
      example: `customer name`,
      exampleExplanation: `This searches for data assets containing words that begin with "customer" and "name"`,
    },
    {
      title: 'Context Search',
      tip: 'Use a colon to limit your search to a certain context',
      example: `customer : crm`,
      exampleExplanation: `This searches for data assets matching "customer" in systems, datasets etc that match "crm"`,
    },
    {
      title: 'Exact Name Search',
      tip: 'Use a space at the end to find assets with exact names',
      example: `customer name (with a space at the end)`,
      exampleExplanation: `This searches for data assets with a name that exactly matches "customer name"`,
    },
    {
      title: 'Phrase Search',
      tip: 'Use quotation marks to search for exact phrase matches',
      example: `"customer name"`,
      exampleExplanation: `This searches for data assets matching exactly the word "customer" directly followed by "name"`,
    },
    {
      title: 'Or Logic',
      tip: 'Use a pipe to match assets with "or" logic',
      example: `customer | name`,
      exampleExplanation: `This searches for data assets containing words that begin with either "customer" or "name"`,
    },
    {
      title: 'Exclude Words',
      tip: 'Use an exclamation mark to exclude words',
      example: `customer ! name`,
      exampleExplanation: `This searches for data assets matching "customer" and that does not include the word "name"`,
    },
  ];

  let attributes = [];
  let attribute_values = [];
  let fields = [];
  let datasets = [];
  let dataset_groups = [];
  let datasources = [];
  let systems = [];
  let entities = [];
  let glossaries = [];
  let users = [];
  let user_groups = [];

  if (searchResults) {
    fields = searchResults.filter(x => x.object_name === 'field');
    datasets = searchResults.filter(x => x.object_name === 'dataset');
    dataset_groups = searchResults.filter(x => x.object_name === 'dataset_group');
    datasources = searchResults.filter(x => x.object_name === 'datasource');
    systems = searchResults.filter(x => x.object_name === 'system');
    entities = searchResults.filter(x => x.object_name === 'entity');
    glossaries = searchResults.filter(x => x.object_name === 'glossary');
    attributes = searchResults.filter(x => x.object_name === 'attribute');
    attribute_values = searchResults.filter(x => x.object_name === 'attribute_value');
    users = searchResults.filter(x => x.object_name === 'user');
    user_groups = searchResults.filter(x => x.object_name === 'user_group');
  }

  const parseSearchTerm = () => {
    if (!searchTerm) return;

    let [first, ...rest] = searchTerm.split(':');
    rest = rest.join(':');

    return `"${first}"` + (rest ? ` in the context of "${rest}"` : '');
  };

  return (
    <div className="Home">
      <div className="search-container">
        <div className={'search-header'}>
          <form onSubmit={onSubmit} autoComplete="off">
            <div className={'search-input' + (invalidSearchTerm ? ' invalid' : '')}>
              <Input
                type="text"
                icon="search"
                name="search"
                isNullable={true}
                ref={refSearchInput}
                placeholder="Search datasets, terms and more..."
                keyboardShortcut="search"
                validation={invalidSearchTerm && { valid: false, message: 'Invalid search string' }}
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
          </form>
        </div>

        {searchTerm.length < 2 && (
          <div
            className={
              'search-tip' + (showSearchTip ? ' show' : '') + (showAllSearchTips ? ' show-all' : '')
            }
          >
            <div className="search-tip-icon">
              <Icon name="tip" />
            </div>
            <div className="search-tip-list">
              <div className="search-tip-text">
                <p className="search-tip-text-description">
                  {searchTipIndex && searchTipList[searchTipIndex - 1].tip}
                </p>
                <p className="search-tip-text-example">
                  Example:&nbsp;&nbsp;
                  <span className="code">
                    {searchTipIndex && searchTipList[searchTipIndex - 1].example}
                  </span>
                </p>
                <p className="search-tip-text-example-explanation">
                  {searchTipIndex && searchTipList[searchTipIndex - 1].exampleExplanation}
                </p>
              </div>
              <div
                className="search-tip-show-all-button"
                ref={refShowAllSearchTipsButton}
                tabIndex={0}
                onClick={toggleShowAllSearchTips}
              >
                {showAllSearchTips ? 'Hide' : 'Show'} all tips
              </div>
            </div>
          </div>
        )}

        {searchResults && searchTerm.length > 1 && (
          <Tabs
            onTabChange={onObjectChange}
            showResultCount={true}
            resultMessage={parseSearchTerm()}
            showNoResultsMessage={false}
            className="vw-50"
            disableTabsWithoutResults={true}
            defaultSelectedTab={{ tab: activeObject }}
            ref={refTabContent}
            loading={loading}
            orientation="vertical"
            tabLabel="Asset Type"
          >
            <div
              label="All"
              tabid="all"
              resultCount={(searchResultsCount && searchResultsCount.all) || 0}
            >
              <SearchResult
                data={searchResults}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={searchResultsCount && searchResultsCount.all}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <hr />

            <div
              label="Glossaries"
              tabid={Asset.Glossary}
              resultCount={(searchResultsCount && searchResultsCount.glossary) || 0}
            >
              <SearchResult
                data={glossaries}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={searchResultsCount && searchResultsCount.glossary}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <div
              label="Entities"
              tabid={Asset.Entity}
              resultCount={(searchResultsCount && searchResultsCount.entity) || 0}
            >
              <SearchResult
                data={entities}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={searchResultsCount && searchResultsCount.entity}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <div
              label="Attributes"
              tabid={Asset.Attribute}
              resultCount={(searchResultsCount && searchResultsCount.attribute) || 0}
            >
              <SearchResult
                data={attributes}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={searchResultsCount && searchResultsCount.attribute}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <div
              label="Attribute Values"
              tabid={Asset.AttributeValue}
              resultCount={(searchResultsCount && searchResultsCount.attribute_value) || 0}
            >
              <SearchResult
                data={attribute_values}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={searchResultsCount && searchResultsCount.attribute_value}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <hr />

            <div
              label="Systems"
              tabid={Asset.System}
              resultCount={(searchResultsCount && searchResultsCount.system) || 0}
            >
              <SearchResult
                data={systems}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={searchResultsCount && searchResultsCount.system}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <div
              label="Datasources"
              tabid={Asset.Datasource}
              resultCount={(searchResultsCount && searchResultsCount.datasource) || 0}
            >
              <SearchResult
                data={datasources}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={searchResultsCount && searchResultsCount.datasource}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <div
              label="Dataset Groups"
              tabid={Asset.DatasetGroup}
              resultCount={(searchResultsCount && searchResultsCount.dataset_group) || 0}
            >
              <SearchResult
                data={dataset_groups}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={(searchResultsCount && searchResultsCount.dataset_group) || 0}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <div
              label="Datasets"
              tabid={Asset.Dataset}
              resultCount={(searchResultsCount && searchResultsCount.dataset) || 0}
            >
              <SearchResult
                data={datasets}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={(searchResultsCount && searchResultsCount.dataset) || 0}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <div
              label="Fields"
              tabid={Asset.Field}
              resultCount={(searchResultsCount && searchResultsCount.field) || 0}
            >
              <SearchResult
                data={fields}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={searchResultsCount && searchResultsCount.field}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <hr />

            <div
              label="User Groups"
              tabid={Asset.UserGroup}
              resultCount={(searchResultsCount && searchResultsCount.user_group) || 0}
            >
              <SearchResult
                data={user_groups}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={searchResultsCount && searchResultsCount.user_group}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>

            <div
              label="Users"
              tabid={Asset.User}
              resultCount={(searchResultsCount && searchResultsCount.user) || 0}
            >
              <SearchResult
                data={users}
                loadMore={loadMore}
                containerElement={refTabContent.current}
                resultCount={searchResultsCount && searchResultsCount.user}
                loading={loading}
                searchTerm={parseSearchTerm()}
              />
            </div>
          </Tabs>
        )}

        {searchTerm.length < 2 && !showAllSearchTips && (
          <React.Fragment>
            <div className="large-nav-links">
              <div className="large-nav-links-title">WHAT'S IN KATALOGUE</div>

              <div className="large-nav-link-container-wrapper">
                <div className="large-nav-link-container">
                  <div className="large-nav-link-wrapper">
                    <div className="large-nav-link">
                      <div className="large-nav-link-icon">
                        <Icon name="datasets" />
                      </div>
                      <div className="large-nav-link-title">Datasets</div>
                      <div className="large-nav-link-description">
                        The data your organization actually holds, from systems and tables to files,
                        APIs and fields.
                      </div>
                      <div className="large-nav-link-cta">
                        <Button
                          to="/systems"
                          className="main-button"
                          icon="arrow_right"
                          value="Browse Datasets"
                        />
                      </div>
                    </div>

                    {/* Under the card rather than in it, so the card ends on its button. Rendered
                        empty until the figures arrive, so nothing moves when they do. */}
                    <div className="large-nav-link-count">
                      {assetCount && (
                        <React.Fragment>
                          {formatData(assetCount.dataset_count, 'integer')}{' '}
                          {label(assetCount.dataset_count, 'dataset', 'datasets')} in{' '}
                          {formatData(assetCount.system_count, 'integer')}{' '}
                          {label(assetCount.system_count, 'system', 'systems')}
                        </React.Fragment>
                      )}
                    </div>
                  </div>

                  <div className="large-nav-link-wrapper">
                    <div className="large-nav-link">
                      <div className="large-nav-link-icon">
                        <Icon name="glossaries" />
                      </div>
                      <div className="large-nav-link-title">Glossaries</div>
                      <div className="large-nav-link-description">
                        The words your organization uses, what they mean, and where the data they
                        describe lives.
                      </div>
                      <div className="large-nav-link-cta">
                        <Button
                          to="/glossaries"
                          className="main-button"
                          icon="arrow_right"
                          value="Browse Glossaries"
                        />
                      </div>
                    </div>

                    <div className="large-nav-link-count">
                      {assetCount && (
                        <React.Fragment>
                          {/* Entities and attributes together: "term" is what they are to a reader
                              who has not learned the two words yet. */}
                          {formatData(
                            assetCount.entity_count + assetCount.attribute_count,
                            'integer'
                          )}{' '}
                          {label(
                            assetCount.entity_count + assetCount.attribute_count,
                            'term',
                            'terms'
                          )}{' '}
                          in {formatData(assetCount.glossary_count, 'integer')}{' '}
                          {label(assetCount.glossary_count, 'glossary', 'glossaries')}
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {ownedItemsCount > 0 && (
                <div className="large-nav-links-footer">
                  <Button
                    to="/profile"
                    tooltip="See all assets you are assigned to"
                    value={
                      <React.Fragment>
                        Your {label(ownedItemsCount, 'Asset', 'Assets')}
                        <span className="large-nav-links-footer-count">
                          {formatData(ownedItemsCount, 'integer')}
                        </span>
                      </React.Fragment>
                    }
                  />
                </div>
              )}
            </div>
          </React.Fragment>
        )}
        {searchTerm.length < 2 && showAllSearchTips && (
          <React.Fragment>
            <div className="large-nav-links">
              <div className="large-nav-links-title">SEARCH TIPS</div>

              <div className="large-nav-link-container-wrapper">
                <div className="large-nav-link-container">
                  {searchTipList.map((item, index) => {
                    return (
                      <div key={index} className="search-tip-large-text">
                        <p className="search-tip-large-text-title">{item.title}</p>
                        <p className="search-tip-large-text-description">{item.tip}</p>
                        <p className="search-tip-large-text-example">
                          Example:&nbsp;&nbsp;<span className="code">{item.example}</span>
                        </p>
                        <p className="search-tip-large-text-example-explanation">
                          {item.exampleExplanation}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};
